import type { XYPosition } from '@xyflow/react';

/** Minimum radial distance between two consecutive rings. */
export const RING_GAP = 260;
/** Free space kept between the widest nodes of one ring. */
export const RING_NODE_GAP = 40;

/**
 * Order of the kinds on a ring: the real kinds, then kinds added after this release, then the group nodes.
 * The project node is always the root, so it never sits on a ring.
 */
const KIND_ORDER: Record<string, number> = {
  aircraft: 0,
  event: 1,
  engine: 2,
  reference: 3,
  document: 4,
  part: 5,
  ipc: 6,
  group: 8,
};
const UNKNOWN_KIND_RANK = 7;
export const kindRank = (kind: string) => KIND_ORDER[kind] ?? UNKNOWN_KIND_RANK;

type LayoutNode = { id: string; kind: string };
type LayoutEdge = { from: string; to: string };

const byKindThenId = (a: LayoutNode, b: LayoutNode) =>
  kindRank(a.kind) - kindRank(b.kind) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const TWO_PI = 2 * Math.PI;
const START_ANGLE = -Math.PI / 2;

type Size = { width: number; height: number };

/** Undirected adjacency over the edges whose ends are both in `nodes`. */
function adjacency(nodes: readonly LayoutNode[], edges: readonly LayoutEdge[]): Map<string, string[]> {
  const adjacent = new Map<string, string[]>(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (!adjacent.has(e.from) || !adjacent.has(e.to)) continue;
    adjacent.get(e.from)?.push(e.to);
    adjacent.get(e.to)?.push(e.from);
  }
  return adjacent;
}

/** Hop distance from the centre by breadth-first search. Unreached nodes are absent. */
function hopDistances(adjacent: ReadonlyMap<string, string[]>, centreId: string): Map<string, number> {
  const distance = new Map<string, number>([[centreId, 0]]);
  const queue = [centreId];
  // The array iterator reads the length on every step, so it also visits the ids pushed below.
  for (const id of queue) {
    const next = (distance.get(id) ?? 0) + 1;
    for (const neighbour of adjacent.get(id) ?? []) {
      if (distance.has(neighbour)) continue;
      distance.set(neighbour, next);
      queue.push(neighbour);
    }
  }
  return distance;
}

/**
 * Circular mean angle of a node's neighbours on the ring inside, measured clockwise
 * from the top so it sorts in the direction the ring is filled. `null` when it has none.
 */
function barycentreKey(neighbours: readonly string[], angleOf: ReadonlyMap<string, number>): number | null {
  const angles = neighbours.flatMap((id) => {
    const angle = angleOf.get(id);
    return angle === undefined ? [] : [angle];
  });
  if (angles.length === 0) return null;
  const x = angles.reduce((sum, a) => sum + Math.cos(a), 0);
  const y = angles.reduce((sum, a) => sum + Math.sin(a), 0);
  const key = (((Math.atan2(y, x) - START_ANGLE) % TWO_PI) + TWO_PI) % TWO_PI;
  // Rounding can put an angle at the top just below 2π; it belongs at the start.
  return TWO_PI - key < 1e-9 ? 0 : key;
}

type Placed<N extends LayoutNode> = { n: N; angle: number };

/** Spreads the nodes evenly over the ring, in the given order, clockwise from the top. */
const evenSpread = <N extends LayoutNode>(ordered: N[]): Placed<N>[] =>
  ordered.map((n, i) => ({ n, angle: START_ANGLE + (TWO_PI * i) / ordered.length }));

/** Middle of the widest empty arc between the sorted angles in [0, 2π), counting the wrap past 2π. */
function widestGapMiddle(sortedAngles: readonly number[]): number {
  if (sortedAngles.length === 0) return 0;
  const gaps = sortedAngles.map((a, i) => ({
    from: a,
    size: (i + 1 < sortedAngles.length ? sortedAngles[i + 1] : sortedAngles[0] + TWO_PI) - a,
  }));
  const widest = gaps.reduce((best, g) => (g.size > best.size ? g : best));
  return widest.from + widest.size / 2;
}

/**
 * Places each node of an outer ring as near the angle of its neighbours on the ring
 * inside as a minimum gap allows. Nodes that share a parent angle are centred on it,
 * one gap apart. The sweep starts in the widest empty arc, so no group straddles its
 * start; each node moves clockwise only as far as it must to clear the one before,
 * and a backward pass pulls the end back if it would run into the start. Only a ring
 * with no room for one gap per node is spread evenly instead.
 */
function nearParents<N extends LayoutNode>(
  ring: N[],
  adjacent: ReadonlyMap<string, string[]>,
  angleOf: ReadonlyMap<string, number>,
  gap: number,
): Placed<N>[] {
  const keyed = ring.map((n) => ({ n, key: barycentreKey(adjacent.get(n.id) ?? [], angleOf) }));
  const withKey = keyed.flatMap(({ n, key }) => (key === null ? [] : [{ n, key }]));
  // Only unreached nodes have no neighbour on the ring inside; they go last.
  const keyless = keyed.flatMap(({ n, key }) => (key === null ? [n] : [])).sort(byKindThenId);
  const byKey = (a: { n: N; key: number }, b: { n: N; key: number }) => a.key - b.key || byKindThenId(a.n, b.n);
  if (ring.length * gap > TWO_PI - 1e-9) return evenSpread([...[...withKey].sort(byKey).map(({ n }) => n), ...keyless]);

  const origin = widestGapMiddle([...new Set(withKey.map(({ key }) => key))].sort((a, b) => a - b));
  const rotated = withKey.map(({ n, key }) => ({ n, key: (((key - origin) % TWO_PI) + TWO_PI) % TWO_PI })).sort(byKey);
  const runLength = new Map<number, number>();
  rotated.forEach(({ key }) => runLength.set(key, (runLength.get(key) ?? 0) + 1));
  const seen = new Map<number, number>();
  const targets = rotated.map(({ key }) => {
    const index = seen.get(key) ?? 0;
    seen.set(key, index + 1);
    return key + (index - ((runLength.get(key) ?? 1) - 1) / 2) * gap;
  });
  const lastTarget = targets.length > 0 ? targets[targets.length - 1] : 0;
  const all = [...targets, ...keyless.map(() => lastTarget)];

  const forward: number[] = [];
  all.forEach((target, i) => forward.push(i === 0 ? target : Math.max(target, forward[i - 1] + gap)));
  // The last node must also clear the first one across the start.
  const swept = [...forward];
  forward.reduceRight((next, angle, i) => {
    swept[i] = Math.min(angle, next - gap);
    return swept[i];
  }, forward[0] + TWO_PI);
  return [...rotated.map(({ n }) => n), ...keyless].map((n, i) => ({ n, angle: START_ANGLE + origin + swept[i] }));
}

/**
 * Places the centre at (0, 0) and every other node on the ring whose index is its hop
 * distance from the centre over `edges`; a node the centre cannot reach goes one ring further out.
 * Ring 1 is spread evenly by kind then id, clockwise from the top; outer rings sit
 * near their neighbours on the ring inside. Returns React Flow top-left positions.
 * Deterministic and synchronous.
 */
export function concentricLayout<N extends LayoutNode>(
  nodes: N[],
  edges: readonly LayoutEdge[],
  centreId: string,
  sizeOf: (kind: N['kind']) => Size,
): Map<string, XYPosition> {
  const adjacent = adjacency(nodes, edges);
  const distance = hopDistances(adjacent, centreId);
  const outermost = Math.max(0, ...nodes.map((n) => distance.get(n.id) ?? 0));
  const ringOf = (id: string) => distance.get(id) ?? outermost + 1;
  const rings = Array.from({ length: outermost + 2 }, (_, k) => nodes.filter((n) => ringOf(n.id) === k));

  const positions = new Map<string, XYPosition>();
  const angleOf = new Map<string, number>();
  rings.reduce((innerRadius, ring, k) => {
    if (ring.length === 0) return innerRadius;
    const count = ring.length;
    const slot = Math.max(...ring.map((n) => sizeOf(n.kind).width)) + RING_NODE_GAP;
    // The chord between neighbours, 2r·sin(π/n), must be at least one slot so no two nodes overlap.
    const radius =
      k === 0 ? 0 : Math.max(innerRadius + RING_GAP, count >= 2 ? slot / (2 * Math.sin(Math.PI / count)) : 0);
    // The angle whose chord is one slot: the closest two nodes on this ring may sit.
    const gap = 2 * Math.asin(Math.min(1, slot / (2 * Math.max(radius, 1))));
    const placed = k <= 1 ? evenSpread([...ring].sort(byKindThenId)) : nearParents(ring, adjacent, angleOf, gap);
    placed.forEach(({ n, angle }) => {
      const { width, height } = sizeOf(n.kind);
      angleOf.set(n.id, angle);
      positions.set(n.id, { x: radius * Math.cos(angle) - width / 2, y: radius * Math.sin(angle) - height / 2 });
    });
    return radius;
  }, 0);
  return positions;
}
